import { useEffect, useLayoutEffect } from "react";
import PromoShowcase from "./PromoShowcase";
import LeadQuiz from "./LeadQuiz";
import TemplateShowcase, { getRequestedStyle } from "./TemplateShowcase";
import { SiteHeader, SiteFooter } from "./SiteChrome";
import { references } from "./portfolio";
import "./agency.css";

export default function App() {
  const requestedStyle = getRequestedStyle();

  useLayoutEffect(() => {
    // React mounts the anchor targets after the browser's initial fragment lookup.
    const target = document.getElementById(window.location.hash.slice(1));
    target?.scrollIntoView({ behavior: "instant", block: "start" });
  }, []);

  useEffect(() => {
    // Decoration only. Leave content visible if observers are unsupported or JS is disabled.
    if (!("IntersectionObserver" in window) || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const root = document.querySelector<HTMLElement>(".page main");
    if (!root) return;

    const selectors = [
      ".agency-work-composition",
      ".template-showcase-heading",
      ".style-zone",
      ".reference-section .section-heading",
      ".reference-grid-expanded article",
      "#vizitka-home-title",
      ".service-grid article",
      ".onyx-verticals .vertical-card",
      ".audit-copy",
      ".audit .lead-quiz"
    ].join(", ");
    const elements = Array.from(root.querySelectorAll<HTMLElement>(selectors));

    const reveal = (element: HTMLElement) => {
      element.classList.add("onyx-reveal-visible");
      element.classList.remove("onyx-reveal-pending");
      observer.unobserve(element);
    };
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) reveal(entry.target as HTMLElement);
      }
    }, { threshold: 0.01, rootMargin: "0px 0px 40px 0px" });

    // Some browsers delay observer callbacks during fast anchor/smooth scrolling.
    // Passive rAF fallback keeps every section readable instead of leaving it dimmed.
    let scheduled = 0;
    const checkViewport = () => {
      scheduled = 0;
      for (const element of elements) {
        if (!element.classList.contains("onyx-reveal-pending")) continue;
        const rect = element.getBoundingClientRect();
        if (rect.top < window.innerHeight - 12 && rect.bottom > 0) reveal(element);
      }
    };
    const onViewportChange = () => {
      if (!scheduled) scheduled = window.requestAnimationFrame(checkViewport);
    };
    window.addEventListener("scroll", onViewportChange, { passive: true });
    window.addEventListener("resize", onViewportChange);

    for (const element of elements) {
      const rect = element.getBoundingClientRect();
      // Never flash or conceal the above-the-fold content, anchors or focused targets.
      if (rect.top < window.innerHeight + 15 || element.contains(document.activeElement)) {
        element.classList.add("onyx-reveal-visible");
        continue;
      }
      // Small stagger for portfolio and services, capped to prevent sluggishness.
      if (element.matches(".reference-grid-expanded article, .service-grid article")) {
        const siblings = Array.from(element.parentElement?.children ?? []);
        element.style.setProperty("--onyx-reveal-delay", Math.min(siblings.indexOf(element) % 3, 2) * 85 + "ms");
      }
      element.classList.add("onyx-reveal-pending");
      observer.observe(element);
    }

    onViewportChange();
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onViewportChange);
      window.removeEventListener("resize", onViewportChange);
      if (scheduled) window.cancelAnimationFrame(scheduled);
      for (const element of elements) {
        element.classList.remove("onyx-reveal-pending", "onyx-reveal-visible");
        element.style.removeProperty("--onyx-reveal-delay");
      }
    };
  }, []);

  return (
    <div className="page" id="top">
      <SiteHeader />

      <main>
        <PromoShowcase />
        <TemplateShowcase showHeading />
        <section className="section shell reference-section" id="references">
          <div className="section-heading">
            <p>VYBRANÉ PROJEKTY</p>
            <h2>Každý projekt má svůj vlastní svět.</h2>
            <span className="section-subline">Prohlédněte si weby z ekosystému ONYX. Různé obory, různé potřeby a pokaždé vlastní vizuální rukopis. TEAHUB je koncepční prezentace.</span>
          </div>
          <div className="reference-grid reference-grid-expanded">
            {references.map((project, index) => (
              <article key={project.name}>
                <a href={project.href} target="_blank" rel="noreferrer">
                  <div className="reference-shot">
                    <img src={project.image} alt={project.status === "concept" ? "Vizuální koncept TEAHUB, nikoli screenshot spuštěného webu" : "Aktuální náhled webu " + project.name} loading="lazy" />
                    <span className={"reference-live" + (project.status === "landing" ? " reference-concept" : "")}>{project.status === "landing" ? "PREZENTACE" : "LIVE"}</span>
                  </div>
                  <div className="reference-copy"><b>{project.name}</b><span>{project.meta}</span></div>
                  <i>{String(index + 1).padStart(2, "0")}</i>
                </a>
              </article>
            ))}
          </div>
        </section>

        <section className="section shell" aria-labelledby="vizitka-home-title" style={{ paddingTop: 12, paddingBottom: 60 }}>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr)", gap: 20, border: "1px solid #d6e6f6", borderRadius: 22, padding: "clamp(24px, 4vw, 50px)", background: "linear-gradient(112deg,#f0f8ff,#fff)" }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".16em", color: "#1376d7" }}>NOVINKA / WEBOVÁ VIZITKA</div>
            <h2 id="vizitka-home-title" style={{ maxWidth: 720, margin: 0, fontFamily: "Georgia,serif", fontSize: "clamp(30px,4vw,52px)", lineHeight: 1.1 }}>Vlastní webová vizitka. <span style={{ color: "#0b77e8" }}>Začněte být vidět.</span></h2>
            <p style={{ maxWidth: 660, margin: 0, color: "#5f758d", lineHeight: 1.8 }}>ONYXWEB Vizitka+ je jednoduchá cesta k profesionální online prezentaci. Ukážeme vám skutečný příklad Pedikúry Dita na notebooku, iPadu a mobilu, včetně základů SEO a sdílení.</p>
            <div><a href="/vizitka" className="primary" style={{ borderRadius: 40 }}>Prohlédnout Vizitka+ →</a></div>
          </div>
        </section>

        <section className="section shell" id="services">
          <div className="section-heading">
            <p>OD STRATEGIE PO RŮST</p>
            <h2>Web, který zaujme. Systém, který pracuje pro vás.</h2>
          </div>
          <div className="service-grid">
            <article><span>01</span><h3>Strategie a UX</h3><p>Struktura, nabídka a cesta návštěvníka postavená kolem reálné konverze.</p></article>
            <article><span>02</span><h3>Design a vývoj</h3><p>Prémiový responzivní web s čistou technikou a rychlým načítáním.</p></article>
            <article><span>03</span><h3>Automatizace</h3><p>Leady, objednávky, CRM a další procesy propojené bez zbytečné ruční práce.</p></article>
            <article><span>04</span><h3>Růst a optimalizace</h3><p>Měření, experimenty a průběžné vylepšování podle skutečných výsledků.</p></article>
          </div>
        </section>

        <section className="section shell onyx-verticals" aria-label="ONYXWEB vertikální SaaS">
          <div className="vertical-card gastro-teaser">
            <div>
              <p>ONYXGASTRO+ / PRO RESTAURACE A ČAJOVNY</p>
              <h2>ONYXGASTRO+</h2>
              <h3>Web, QR menu, objednávky, rezervace, CRM a růst v jednom systému.</h3>
              <div className="vertical-pills">
                <span>QR Menu</span><span>Table Orders</span><span>Reservations</span><span>CRM</span><span>ONYX Pay</span><span>AI</span>
              </div>
            </div>
            <div className="vertical-pricing">
              <small>od</small><strong>690 Kč</strong><span>/ měsíc</span>
              <a className="primary" href="/gastro">Prohlédnout ONYXGASTRO+ →</a>
            </div>
          </div>
        </section>

        <section className="audit shell" id="audit">
          <div className="audit-copy">
            <p>VÁŠ PROJEKT MŮŽE ZAČÍT TADY</p>
            <h2>Povězte nám, co máte v hlavě.</h2>
            <span>Stačí nápad, odkaz na současný web nebo pár vět o tom, co chcete změnit. Navrhneme další krok.</span>
          </div>
          <LeadQuiz service="web-design" source="onyxweb.cz" heading="Co má váš nový web změnit?" initialGoal={requestedStyle ? "Zajímá mě vizuální směr " + requestedStyle.toUpperCase() + "." : ""}
            options={[
              { value: "new-web", label: "Nový web / landing page" },
              { value: "redesign", label: "Redesign stávajícího webu" },
              { value: "conversion", label: "Více poptávek a prodejů" },
              { value: "automation", label: "Propojení s CRM a automatizací" }
            ]} />
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
